import React, {
  useContext, ReactElement,
} from 'react';
import { Accordion, AccordionContext } from 'react-bootstrap';
import clsx from 'clsx';
import { Popover } from './Popover';
import { useSessionStorage } from '../../hooks/useSessionStorage';

interface StorageSession {
  sessionId: number;
  prefix: string
}
interface ControlledAccordionProps {
  children: React.ReactNode;
  defaultActiveKey?: string;
  className?: string;
  storageSession?: StorageSession
}

export interface ControlledAccordionContextValues {
  visitedKeys: Set<string>;
  markVisited: (key: string) => void;
  wasKeyVisited: (key: string) => boolean;
}

// This context is used to track which accordion items have been visited
// But it could be extended to track other states as well
const ControlledAccordionContext = React.createContext<ControlledAccordionContextValues>({
  visitedKeys: new Set(),
  markVisited: () => {},
  wasKeyVisited: () => false,
});

// Custom hook to use the ControlledAccordionContext
export function useControlledAccordionContext() {
  const context = useContext(ControlledAccordionContext);
  if (!context) {
    throw new Error('useControlledAccordionContext must be used within a FileTreeContextProvider');
  }
  return context;
}

// Provider component that manages and supplies visited item state to ControlledAccordion.
// Supports both local state and session storage, enabling persistence across page navigation.
// Uses session storage when a storageSession is provided, otherwise falls back to in-memory state.
export function ControlledAccordionProvider({
  children,
  storageSession,
  defaultActiveKey,
}: {
  children: React.ReactNode,
  storageSession?: StorageSession,
  defaultActiveKey?: string
}) {
  // Use session storage if storageSession is provided
  const storageKey = storageSession
    ? `${storageSession.prefix}_${storageSession.sessionId}`
    : null;

  // Initialize local state for when not using session storage
  // If defaultActiveKey exists, initialize with that key already in the Set
  const [localVisitedKeys, setLocalVisitedKeys] = React.useState<Set<string>>(
    () => (defaultActiveKey ? new Set([defaultActiveKey]) : new Set())
  );
  // Set default values if defaultActiveKey exists
  const initialSessionValue = defaultActiveKey ? [defaultActiveKey] : [];

  // Only initialize session storage if storageKey exists
  const sessionStore = storageKey
    ? useSessionStorage<string[]>(storageKey, initialSessionValue)
    : null;

  // Create memoized functions for managing visited keys
  const markVisited = React.useCallback((key: string) => {
    if (sessionStore) {
      // Session storage approach
      if (!sessionStore.value.includes(key)) {
        sessionStore.setValue([...sessionStore.value, key]);
      }
    } else {
      // Local state approach
      setLocalVisitedKeys((prev) => new Set(prev).add(key));
    }
  }, [sessionStore]);

  const wasKeyVisited = React.useCallback((key: string) => {
    if (sessionStore) {
      return sessionStore.value.includes(key);
    }
    return localVisitedKeys.has(key);
  }, [sessionStore, localVisitedKeys]);

  // Set session on render values when already visited
  React.useEffect(() => {
    if (defaultActiveKey && !wasKeyVisited(defaultActiveKey)) {
      markVisited(defaultActiveKey);
    }
  }, [defaultActiveKey]);

  // Create a Set representation of visited keys
  const visitedKeys = React.useMemo(() => {
    if (sessionStore) {
      return new Set(sessionStore.value);
    }
    return localVisitedKeys;
  }, [sessionStore?.value, localVisitedKeys]);

  const value = React.useMemo(() => ({
    visitedKeys,
    markVisited,
    wasKeyVisited,
  }), [visitedKeys, markVisited, wasKeyVisited]);

  return (
    <ControlledAccordionContext.Provider value={value}>
      {children}
    </ControlledAccordionContext.Provider>
  );
}

export function ControlledAccordion({
  children,
  defaultActiveKey = '0',
  className = 'accordion accordion-pills',
  storageSession,
}: ControlledAccordionProps) {
  return (
    <ControlledAccordionProvider
      storageSession={storageSession}
      defaultActiveKey={defaultActiveKey}
    >
      <Accordion
        className={className}
        defaultActiveKey={defaultActiveKey}
      >
        {children}
      </Accordion>
    </ControlledAccordionProvider>
  );
}

interface ControlledAccordionItemProps {
  children: React.ReactNode
  eventKey: string
  isFilled: boolean
  isOpenRequired?: boolean
  button: {
    title: string
    tooltip?: {
      title?: string | ReactElement
      content?: string | ReactElement
    }
  }
}
export function ControlledAccordionItem({
  children, eventKey, isFilled, button, isOpenRequired,
}: ControlledAccordionItemProps) {
  const { activeEventKey } = useContext(AccordionContext);
  const { wasKeyVisited, markVisited } = useControlledAccordionContext();

  // Checks if its required to check if the accordion item has been opened
  // In order to show the success state
  const isSuccess = isOpenRequired ? isFilled && wasKeyVisited(eventKey) : isFilled;

  // Managed from 'Native' React bootstrap accordion
  const isActive = activeEventKey === eventKey;

  // Is item open and has a tooltip?
  const showTooltip = isActive && button?.tooltip?.content;

  const handleClick = () => {
    markVisited(eventKey);
  };

  return (
    <Accordion.Item eventKey={eventKey} className={clsx('shadow-none', isActive && 'border border-gray-200')}>
      <Accordion.Button onClick={handleClick} className={clsx('bg-gray-200 shadow-none', isSuccess && 'bg-success-light', isActive && 'bg-transparent')}>
        <span className="pl-4">
          {button.title}
        </span>
        {showTooltip && (
          <Popover
            id={`accordeon-${eventKey}-help`}
            width={362}
            trigger="hover"
            renderTrigger={() => (
              <img
                className="ms-2"
                src="/static/img/icons/help.svg"
                alt="Más información"
              />
            )}
            renderContent={() => (
              <div className="d-flex align-items-start gap-2 p-1">
                <img src="/static/img/icons/help-light.svg" width={48} alt="" />
                <div>
                  <div className="text-white">
                    {button?.tooltip?.title && <h6 className="text-white fs-base">{button?.tooltip?.title}</h6>}
                    {button?.tooltip?.content}
                  </div>
                </div>
              </div>
            )}
          />
        )}
      </Accordion.Button>

      <Accordion.Body className="pl-6 pr-8 pt-0">
        {children}
      </Accordion.Body>
    </Accordion.Item>
  );
}
